// lib/koi-pond-v3/assets/lotusPads.ts
//
// T9.1 — lotus pads floating on the water surface. Low-poly discs with a
// radial slit (the iconic lily-pad notch) + a subtly irregular rim, a
// near-silhouette dark green-black material that catches only a faint
// cool moon rim against the water, and a gentle bob + lateral drift. One
// foreground pad carries a vertical closed lotus bud beside it.
//
// Each pad is a Group (pad mesh + optional bud child) so animating the
// group transform bobs/drifts the bud along with its pad. Built on the
// shared fog uniforms so distant pads recede into the teal like every
// other v3 surface.

import {
  BufferGeometry,
  BufferAttribute,
  ShaderMaterial,
  Mesh,
  Group,
  Color,
  Vector3,
  DoubleSide,
} from 'three';
import { palette, type PaletteKey } from '../palette';
import { DEPTH_FOG_UNIFORM_DECL, DEPTH_FOG_APPLY_SNIPPET } from '../shaders/depthFog';

type FogUniforms = { uFogColor: { value: Color }; uFogDensity: { value: number } };

// ── Pad geometry — a flat disc with a slit + irregular rim, slight cup ──
function buildPadGeometry(radius: number, seed: number): BufferGeometry {
  const N = 28;
  const RIM_LIFT = 0.022; // edges curl up a touch (floating cup)
  const notchStart = 0.4 + (seed % 1) * 1.2; // where the slit sits
  const notchWidth = 0.42; // ~24° wedge removed → the lily-pad slit

  const positions: number[] = [];
  const center = 0;
  positions.push(0, 0, 0); // center vertex

  const rimIdx: number[] = [];
  for (let i = 0; i <= N; i++) {
    const theta = (i / N) * Math.PI * 2;
    // skip the wedge → leaves an open slit from rim to centre
    const rel = (theta - notchStart + Math.PI * 4) % (Math.PI * 2);
    if (rel < notchWidth) continue;
    const r =
      radius *
      (1 + 0.08 * Math.sin(3 * theta + seed) + 0.045 * Math.sin(7 * theta + seed * 1.7));
    rimIdx.push(positions.length / 3);
    positions.push(Math.cos(theta) * r, RIM_LIFT, Math.sin(theta) * r);
  }

  const indices: number[] = [];
  for (let k = 0; k < rimIdx.length - 1; k++) {
    // connect adjacent rim verts only — the straddling pair across the
    // slit is never joined, leaving the notch open
    indices.push(center, rimIdx[k], rimIdx[k + 1]);
  }

  const geom = new BufferGeometry();
  geom.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  return geom;
}

// ── Bud geometry — a small vertical closed bud (teardrop), pointed top ──
function buildBudGeometry(): BufferGeometry {
  const H = 0.2;
  const RINGS = 5;
  const RADIAL = 8;
  const MAXR = 0.052;
  const positions: number[] = [];
  const ringStart: number[] = [];
  for (let j = 0; j <= RINGS; j++) {
    const v = j / RINGS;
    const y = v * H;
    const r = MAXR * Math.pow(Math.sin(v * Math.PI), 0.7); // 0 at base + tip, bulge mid
    ringStart.push(positions.length / 3);
    for (let i = 0; i <= RADIAL; i++) {
      const a = (i / RADIAL) * Math.PI * 2;
      positions.push(Math.cos(a) * r, y, Math.sin(a) * r);
    }
  }
  const indices: number[] = [];
  for (let j = 0; j < RINGS; j++) {
    for (let i = 0; i < RADIAL; i++) {
      const a = ringStart[j] + i;
      const b = a + 1;
      const c = ringStart[j + 1] + i;
      const d = c + 1;
      indices.push(a, b, c, b, d, c);
    }
  }
  const geom = new BufferGeometry();
  geom.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();
  return geom;
}

// ── Near-silhouette material: dark base + faint moon top-light + cool rim ──
function makeLotusMaterial(
  baseKey: PaletteKey,
  rimStrength: number,
  moonDir: Vector3,
  fog: FogUniforms,
): ShaderMaterial {
  return new ShaderMaterial({
    side: DoubleSide,
    uniforms: {
      uMoonDir: { value: moonDir.clone().normalize() },
      uMoonColor: { value: new Color(palette.moonlight.hex) },
      uBase: { value: new Color(palette[baseKey].hex) },
      uRim: { value: new Color(palette.rimLight.hex) },
      uRimStrength: { value: rimStrength },
      uFogColor: fog.uFogColor,
      uFogDensity: fog.uFogDensity,
    },
    vertexShader: `
      varying vec3 vWorldPos;
      varying vec3 vNormalW;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorldPos = wp.xyz;
        vNormalW = normalize(mat3(modelMatrix) * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      precision highp float;
      uniform vec3 uMoonDir;
      uniform vec3 uMoonColor;
      uniform vec3 uBase;
      uniform vec3 uRim;
      uniform float uRimStrength;
      ${DEPTH_FOG_UNIFORM_DECL}
      varying vec3 vWorldPos;
      varying vec3 vNormalW;
      void main() {
        vec3 N = normalize(vNormalW);
        vec3 V = normalize(cameraPosition - vWorldPos);
        if (dot(N, V) < 0.0) N = -N;
        vec3 L = normalize(uMoonDir);
        // near-silhouette: dark base with only a faint moon top-sheen
        float diff = clamp(dot(N, L), 0.0, 1.0);
        vec3 color = uBase * (0.35 + 0.5 * diff) * uMoonColor;
        // cool fresnel rim picks the edge out against the water
        float fres = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 3.0);
        color += uRim * fres * uRimStrength;
        ${DEPTH_FOG_APPLY_SNIPPET}
        gl_FragColor = vec4(color, 1.0);
      }
    `,
  });
}

interface PadSpec {
  pos: [number, number, number];
  radius: number;
  yaw: number;
  bud?: boolean;
}

// Asymmetric distribution across the visible foreground/midground water.
const PAD_SPECS: PadSpec[] = [
  { pos: [-1.9, 1.0, 2.1], radius: 0.62, yaw: 0.4, bud: true }, // foreground-left + bud
  { pos: [1.3, 1.0, 2.7], radius: 0.5, yaw: -0.7 }, // foreground-right
  { pos: [-0.2, 1.0, 0.4], radius: 0.42, yaw: 1.1 }, // midground
  { pos: [2.3, 1.0, 0.1], radius: 0.36, yaw: -0.3 }, // right midground
];

export function makeLotusPads(moonDir: Vector3, fog: FogUniforms) {
  const root = new Group();
  const padMat = makeLotusMaterial('lotusDark', 0.5, moonDir, fog);
  const budMat = makeLotusMaterial('lotusBud', 0.3, moonDir, fog);

  const units: Array<{ group: Group; base: Vector3; phase: number; baseYaw: number }> = [];

  PAD_SPECS.forEach((spec, i) => {
    const group = new Group();
    const pad = new Mesh(buildPadGeometry(spec.radius, 1.7 + i * 2.3), padMat);
    group.add(pad);
    if (spec.bud) {
      const bud = new Mesh(buildBudGeometry(), budMat);
      bud.position.set(spec.radius * 0.55, 0, spec.radius * 0.35); // beside the pad
      group.add(bud);
    }
    group.position.set(spec.pos[0], spec.pos[1], spec.pos[2]);
    group.rotation.y = spec.yaw;
    root.add(group);
    units.push({
      group,
      base: new Vector3(spec.pos[0], spec.pos[1], spec.pos[2]),
      phase: i * 1.7,
      baseYaw: spec.yaw,
    });
  });

  function update(t: number) {
    for (const u of units) {
      // gentle bob + slow lateral drift + a touch of yaw sway
      u.group.position.y = u.base.y + 0.03 * Math.sin(t * 0.55 + u.phase);
      u.group.position.x = u.base.x + 0.1 * Math.sin(t * 0.13 + u.phase * 1.3);
      u.group.position.z = u.base.z + 0.08 * Math.cos(t * 0.11 + u.phase * 0.7);
      u.group.rotation.y = u.baseYaw + 0.12 * Math.sin(t * 0.09 + u.phase);
    }
  }

  function dispose() {
    padMat.dispose();
    budMat.dispose();
    root.traverse((o) => {
      if (o instanceof Mesh) o.geometry.dispose();
    });
  }

  return { object: root, update, dispose };
}
